import { useCallback, useContext } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ModalContext } from '../ui/components2/ModalManager';
import useCurrentUser from './useCurrentUser';
import { acceptUserRules, USER_CURRENT_KEY } from '../api/public-voissa/users';
import { ACCEPTANCE_TYPES, isAcceptanceRequired, getAcceptanceDate } from '../libs/userAcceptance';

// Nom d'enregistrement dans `modalTypes` (src/index.js).
export const ACCEPTANCE_MODAL = 'AcceptanceModal';

export { ACCEPTANCE_TYPES };

/**
 * Gate des acceptations de la charte (cf. libs/userAcceptance.js).
 *
 * Toute la logique vit ici : lecture de l'état, ouverture de la modale, enregistrement, mise à jour
 * du cache et REPRISE de l'action interrompue. La modale n'est que de la présentation — elle
 * rappelle ce même hook pour `accept`.
 *
 * Différence de fond avec `usePublicationCurfew` et `useForumCertification`, dont il reprend la
 * forme : leur `guard` abandonne l'action, parce que la sortie (se certifier, attendre le matin)
 * prend du temps. Ici la sortie est une case à cocher : l'action est MISE EN ATTENTE et rejouée
 * dès l'acceptation, sinon on obligerait le membre à recliquer sur « Publier » — et à retrouver
 * une saisie qu'il croyait envoyée.
 *
 * @param {string} type - ACCEPTANCE_TYPES.MEDIA ou ACCEPTANCE_TYPES.COMMENT
 * @returns {{ isRequired: boolean, acceptedAt: ?string, guard: function, openAcceptanceModal: function, accept: function, isAccepting: boolean }}
 */
const useAcceptance = (type) => {
    const currentUser = useCurrentUser();
    const queryClient = useQueryClient();
    const { openModal } = useContext(ModalContext);

    // État au moment du RENDU : sert à l'affichage (ex. un libellé de bouton). L'arbitrage réel se
    // fait au clic — le cache `currentUser` peut avoir changé entre-temps, dans cet onglet comme
    // dans un autre.
    const isRequired = isAcceptanceRequired(currentUser, type);
    const acceptedAt = getAcceptanceDate(currentUser, type);

    const { mutateAsync, isLoading: isAccepting } = useMutation(
        () => acceptUserRules(type),
        {
            onSuccess: (data) => {
                // Patch CIBLÉ du cache plutôt qu'une invalidation : /users/me est un endpoint lourd
                // (abonnement, capabilities, niveau, quotas…) et le refetch retarderait la reprise
                // de l'action. La date renvoyée par l'API est la seule chose qui change.
                queryClient.setQueryData([USER_CURRENT_KEY], (old) => {
                    if (!old) return old;
                    return {
                        ...old,
                        acceptances: {
                            ...(old.acceptances || {}),
                            [type]: data?.acceptance?.acceptedAt || new Date().toISOString(),
                        },
                    };
                });
            },
        }
    );

    /**
     * Enregistre l'acceptation. `mutateAsync` et non `mutate` : l'appelant doit pouvoir attendre
     * que l'API ait répondu avant de rejouer l'action — le middleware côté API refuserait une
     * publication partie trop tôt.
     */
    const accept = useCallback(() => mutateAsync(), [mutateAsync]);

    const openAcceptanceModal = useCallback((onAccepted) => {
        openModal(ACCEPTANCE_MODAL, { type, onAccepted });
    }, [openModal, type]);

    /** Recalcule l'état À L'INSTANT de l'appel, pour les handlers (cf. `isRequired` ci-dessus). */
    const isRequiredNow = useCallback(() => isAcceptanceRequired(
        queryClient.getQueryData([USER_CURRENT_KEY]),
        type
    ), [queryClient, type]);

    /**
     * Enveloppe le handler d'un CTA de publication : tant que la charte n'est pas acceptée, on
     * ouvre la modale au lieu de publier, et on rejoue le handler une fois la case cochée.
     *
     * ⚠️ Le handler est rejoué avec SES ARGUMENTS D'ORIGINE, un tour de boucle plus tard. Un
     * handler qui lit `event.currentTarget` (remis à null par React après le tour synchrone) doit
     * capturer ce dont il a besoin avant, ou se contenter de l'état du composant — ce que font
     * tous les CTA de soumission, qui lisent leur formulaire et pas l'événement.
     */
    const guard = useCallback((handler) => (...args) => {
        if (!isRequiredNow()) return handler?.(...args);

        const [event] = args;
        if (typeof event?.preventDefault === 'function') event.preventDefault();

        openAcceptanceModal(() => handler?.(...args));
        return undefined;
    }, [isRequiredNow, openAcceptanceModal]);

    return { isRequired, acceptedAt, guard, openAcceptanceModal, accept, isAccepting };
};

export default useAcceptance;
